'use client'

import { Cell, Image, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import FollowButton from '@marketplace-web/user-follow/follow-button-feature'

import { useCountdown } from '../../hooks/useCountdown'

type Props = {
  userId: number
  ownerLogin: string
  isFollowed: boolean
  endDate: string
  isHatePresent: boolean
}

const ItemCountdownBanner = ({ userId, ownerLogin, isFollowed, endDate, isHatePresent }: Props) => {
  const { formattedRemainingTime } = useCountdown(endDate)
  const translate = useTranslate('closet_countdown')
  const currentUserId = useSession().user?.id
  const asset = useAsset('/assets/closet-countdown')

  const showFollowAction = userId !== currentUserId && !isHatePresent
  const iconPath = asset('clothes-star-64.png', { theme: { dark: '/dark/clothes-star-64.png' } })

  const renderCountdown = () => {
    if (!formattedRemainingTime) return null

    const { days, hours, minutes, seconds } = formattedRemainingTime

    return (
      <ul className="closet-countdown__timer">
        <SeparatedList separator={<Spacer orientation="vertical" />}>
          <li className="closet-countdown__timer-unit">
            <div className="closet-countdown__timer-unit-value">{days}</div>
            <div className="closet-countdown__timer-unit-name">
              {translate('time.days', undefined, { count: days })}
            </div>
          </li>
          <li className="closet-countdown__timer-unit">
            <div className="closet-countdown__timer-unit-value">{hours}</div>
            <div className="closet-countdown__timer-unit-name">
              {translate('time.hours', undefined, { count: hours })}
            </div>
          </li>
          <li className="closet-countdown__timer-unit">
            <div className="closet-countdown__timer-unit-value">{minutes}</div>
            <div className="closet-countdown__timer-unit-name">
              {translate('time.minutes', undefined, { count: minutes })}
            </div>
          </li>
          <li className="closet-countdown__timer-unit">
            <div className="closet-countdown__timer-unit-value">{seconds}</div>
            <div className="closet-countdown__timer-unit-name">
              {translate('time.seconds', undefined, { count: seconds })}
            </div>
          </li>
        </SeparatedList>
      </ul>
    )
  }

  return (
    <section>
      <Spacer size="large" />
      <Cell
        styling="tight"
        suffix={
          <div className="closet-countdown__image">
            <Image src={iconPath} size="x-large" alt="" styling="rounded" />
          </div>
        }
      >
        <Text as="span" theme="amplified">
          {translate('subtitle')}
        </Text>
        <Spacer />
        {renderCountdown()}
        {showFollowAction && (
          <>
            <Spacer size="x-large" />
            <Text as="h2" type="title">
              {translate('title', { username: ownerLogin })}
            </Text>
          </>
        )}
        <Spacer size="small" />
        <Text type="caption" as="p" html>
          {translate('disclaimer')}
        </Text>
        <Spacer size="x-large" />
      </Cell>
      {showFollowAction && (
        <>
          <FollowButton userId={userId} username={ownerLogin} isActive={isFollowed} size="medium" />
          <Spacer />
        </>
      )}
    </section>
  )
}

export default ItemCountdownBanner
